/**
 * Human-readable surface layer for AI-chat tool calls.
 *
 * Three places rendered raw internals straight into the DOM: the streaming
 * status line showed the bare tool id with no mapping at all
 * (`thinking-indicator.tsx`); the tool-call chip fell back to
 * `toolName.replace(/_/g, " ")` for any of the 953-of-998 tools missing from
 * a 45-entry hand map (`tool-result-card.tsx`); and the verification dialog
 * printed raw argument keys in monospace with `JSON.stringify` for anything
 * non-scalar (`verification-dialog.tsx`).
 *
 * The backend now attaches an authored `toolLabel` to SSE tool events, the
 * human-verification event, and persisted `ai_messages.toolCalls` rows — but
 * conversations that predate that change carry no `toolLabel`, so every
 * caller here must tolerate it being absent. This module is the one place
 * that resolves "what do we show the human" so the fallback rule is only
 * implemented once.
 */

// ── Tool name -> label ──────────────────────────────────────────────────────

/**
 * Turns a machine tool id ("content_studio_session_create" or
 * "recipientBulkDelete") into a lowercase-words sentence fragment. Never
 * returns the raw identifier and never returns a string containing an
 * underscore or hyphen.
 */
export function humanizeToolName(toolName: string | undefined | null): string {
  const trimmed = (toolName ?? '').trim()
  if (!trimmed) return 'this action'
  const spaced = trimmed
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase()
  if (!spaced) return 'this action'
  return spaced.charAt(0).toUpperCase() + spaced.slice(1)
}

/**
 * Prefers the server-authored `toolLabel`; falls back to `humanizeToolName`
 * when it is absent, null, or blank (historical conversations, or a tool
 * the manifest hasn't labelled yet). A trailing defensive strip means the
 * "never an underscore" guarantee holds for the whole resolver, not just
 * the fallback branch — an authored label should never contain one, but a
 * manifest typo must not resurrect the raw-identifier bug this exists to
 * close.
 */
const VISHING_TOOL_LABEL_KEYS: Record<string, string> = {
  vishing_report_overview: 'vishing.ai.labels.reportOverview', vishing_report_departments: 'vishing.ai.labels.reportDepartments', vishing_report_employees: 'vishing.ai.labels.reportEmployees', vishing_report_employee_history: 'vishing.ai.labels.reportHistory', vishing_report_performance: 'vishing.ai.labels.reportPerformance', vishing_report_activity: 'vishing.ai.labels.reportActivity', vishing_report_skipped: 'vishing.ai.labels.reportSkipped',
  vishing_campaign_list: 'vishing.ai.labels.campaignList', vishing_campaign_get: 'vishing.ai.labels.campaignGet', vishing_campaign_create: 'vishing.ai.labels.campaignCreate', vishing_campaign_update: 'vishing.ai.labels.campaignUpdate', vishing_campaign_delete: 'vishing.ai.labels.campaignDelete',
  vishing_campaign_launch: 'vishing.ai.labels.campaignLaunch', vishing_campaign_pause: 'vishing.ai.labels.campaignPause', vishing_campaign_resume: 'vishing.ai.labels.campaignResume', vishing_campaign_terminate: 'vishing.ai.labels.campaignTerminate', vishing_campaign_audience_preview: 'vishing.ai.labels.audiencePreview', vishing_campaign_attempt_list: 'vishing.ai.labels.attemptList', vishing_campaign_target_list: 'vishing.ai.labels.targetList',
  vishing_voice_list: 'vishing.ai.labels.voiceList', vishing_voice_get: 'vishing.ai.labels.voiceGet', vishing_voice_create: 'vishing.ai.labels.voiceCreate', vishing_voice_update: 'vishing.ai.labels.voiceUpdate', vishing_voice_delete: 'vishing.ai.labels.voiceDelete', vishing_voice_clone: 'vishing.ai.labels.voiceClone',
  vishing_scenario_list: 'vishing.ai.labels.scenarioList', vishing_scenario_get: 'vishing.ai.labels.scenarioGet', vishing_scenario_create: 'vishing.ai.labels.scenarioCreate', vishing_scenario_update: 'vishing.ai.labels.scenarioUpdate', vishing_scenario_delete: 'vishing.ai.labels.scenarioDelete', vishing_scenario_clone: 'vishing.ai.labels.scenarioClone',
  vishing_library_capabilities_get: 'vishing.ai.labels.capabilities', vishing_customer_domain_list: 'vishing.ai.labels.domains', vishing_recipient_list: 'vishing.ai.labels.recipients', vishing_recipient_group_list: 'vishing.ai.labels.groups', vishing_target_preview: 'vishing.ai.labels.targetPreview',
  vishing_report_summary: 'vishing.ai.labels.reportSummary', vishing_settings_get: 'vishing.ai.labels.settingsGet', vishing_settings_update: 'vishing.ai.labels.settingsUpdate', vishing_company_settings_update: 'vishing.ai.labels.companySettingsUpdate', vishing_transport_list: 'vishing.ai.labels.transportList', vishing_readiness_get: 'vishing.ai.labels.readiness', vishing_outbound_readiness_get: 'vishing.ai.labels.outboundReadiness', vishing_runtime_status_get: 'vishing.ai.labels.runtime',
};
export function resolveToolLabel(toolName: string, toolLabel?: string | null, t?: (key: string) => string): string {
  if (t && VISHING_TOOL_LABEL_KEYS[toolName]) return t(VISHING_TOOL_LABEL_KEYS[toolName]);
  const authored = toolLabel?.trim()
  const resolved = authored ? authored : humanizeToolName(toolName)
  return resolved.includes('_') ? resolved.replace(/_/g, ' ').replace(/\s+/g, ' ').trim() : resolved
}
export function vishingApprovalKey(toolName: string): string | undefined {
  const actions: Record<string, string> = { vishing_campaign_launch: 'vishing.ai.approval.launch', vishing_campaign_resume: 'vishing.ai.approval.resume', vishing_campaign_pause: 'vishing.ai.approval.pause', vishing_campaign_terminate: 'vishing.ai.approval.terminate', vishing_campaign_delete: 'vishing.ai.approval.deleteCampaign', vishing_voice_delete: 'vishing.ai.approval.deleteVoice', vishing_scenario_delete: 'vishing.ai.approval.deleteScenario', vishing_settings_update: 'vishing.ai.approval.settings', vishing_company_settings_update: 'vishing.ai.approval.companySettings' };
  return actions[toolName];
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

/**
 * Vishing campaign actions whose approval must NAME the campaign.
 *
 * Each takes the campaign's uuid as `id`. The generic summary below shows an
 * opaque id shortened ("77777777…") when it is the only argument and drops it
 * altogether when anything else is present, so the approver of a launch saw
 * either eight hex characters or no campaign at all — and never how many
 * people would be called. The verification dialog reads the campaign for these
 * tools and shows its name and that number; `people` says which number.
 */
const VISHING_CAMPAIGN_ACTIONS: Record<string, 'launch' | 'resume' | null> = {
  vishing_campaign_launch: 'launch',
  vishing_campaign_resume: 'resume',
  vishing_campaign_pause: null,
  vishing_campaign_terminate: null,
  vishing_campaign_delete: null,
  vishing_campaign_update: null,
};
export interface VishingCampaignSubject {
  id: string
  /** The domain to read the campaign in, when the call names one. */
  domainId: string | null
  /** 'launch': the people a launch would call. 'resume': the people still to be called. null: no count applies. */
  people: 'launch' | 'resume' | null
}
/** The campaign a Vishing campaign action is about; null for any other tool or an input without a campaign uuid. */
export function vishingCampaignSubject(toolName: string, input: Record<string, unknown>): VishingCampaignSubject | null {
  if (!Object.prototype.hasOwnProperty.call(VISHING_CAMPAIGN_ACTIONS, toolName)) return null
  const id = input.id
  if (typeof id !== 'string' || !UUID_RE.test(id)) return null
  const domainId = typeof input.domainId === 'string' && UUID_RE.test(input.domainId) ? input.domainId : null
  return { id, domainId, people: VISHING_CAMPAIGN_ACTIONS[toolName] }
}

// ── Approved-argument summarisation ─────────────────────────────────────────

/**
 * Tenant-scoping fields present on nearly every tool call. Never the
 * subject of the action from the approver's point of view — always hidden,
 * regardless of value shape.
 */
const ALWAYS_HIDDEN_KEYS = new Set(['workspaceid', 'companyid', 'domainid'])

function isOpaqueIdField(key: string, value: unknown): boolean {
  return /id$/i.test(key) && typeof value === 'string' && UUID_RE.test(value)
}

/**
 * Curated labels for argument keys common across many tools, where the
 * generic conversion is technically accurate but reads like a field name
 * rather than something a human wrote ("cardCount" -> "Card Count" vs.
 * "Cards"). Keep this list small — everything else falls back to
 * `humanizeArgKey`.
 */
const ARG_KEY_LABELS: Record<string, string> = {
  cardCount: 'Cards',
  topic: 'Topic',
  title: 'Title',
  name: 'Name',
  subject: 'Subject line',
  message: 'Message',
  description: 'Description',
  reason: 'Reason',
  locale: 'Language',
  locales: 'Languages',
  tags: 'Tags',
  status: 'Status',
  query: 'Search',
  email: 'Email address',
}

function humanizeArgKey(key: string): string {
  const spaced = key
    .replace(/_/g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .trim()
  const titled = spaced.replace(/\b\w/g, (c) => c.toUpperCase())
  // "Deck Id" reads like a typo — a trailing Id/Ids word names an
  // identifier, not a word, so present it as the acronym.
  return titled.replace(/\bId\b/g, 'ID').replace(/\bIds\b/g, 'IDs')
}

function argKeyLabel(key: string): string {
  return ARG_KEY_LABELS[key] ?? humanizeArgKey(key)
}

/** Lowercase, spaced form of a key, used only to build a plural noun for array counts. */
function spacedLowerKey(key: string): string {
  return key
    .replace(/_/g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .trim()
    .toLowerCase()
}

function truncate(value: string, max = 160): string {
  return value.length > max ? `${value.slice(0, max)}…` : value
}

/** Shortens an opaque uuid to a readable preview rather than dumping all 36 characters. */
export function shortenId(value: string): string {
  return UUID_RE.test(value) ? `${value.slice(0, 8)}…` : truncate(value, 40)
}

/** Renders one argument value in words. Never JSON — arrays/objects are summarised by count. */
function describeArgValue(value: unknown, key: string): string {
  if (value === null || value === undefined) return 'Not set'
  if (typeof value === 'boolean') return value ? 'Yes' : 'No'
  if (typeof value === 'number') return String(value)
  if (typeof value === 'string') return value.trim() ? truncate(value) : '(empty)'
  if (Array.isArray(value)) {
    const noun = spacedLowerKey(key)
    return `${value.length} ${noun.endsWith('s') ? noun : 'items'}`
  }
  if (typeof value === 'object') {
    const count = Object.keys(value as Record<string, unknown>).length
    return `${count} field${count === 1 ? '' : 's'}`
  }
  return truncate(String(value))
}

export interface ApprovedArgument {
  key: string
  label: string
  value: string
}

export interface ApprovedInputResult {
  /** What to render. Empty when there is nothing safe/meaningful to show. */
  args: ApprovedArgument[]
  /**
   * True only when `args` is empty BECAUSE every field was hidden as
   * tenant plumbing (not because the input itself was empty) — the caller
   * must render a fallback sentence in that case, never a blank box, or
   * the human loses the one signal that something is still being approved.
   */
  allHidden: boolean
}

/**
 * Reduces a raw tool-call input object to what a human should be asked to
 * approve.
 *
 * - `workspaceId` / `companyId` / `domainId` are always dropped — tenant
 *   scoping, never the subject of the action.
 * - An opaque UUID-valued `*Id` field is dropped IF something else in the
 *   input already names the subject (e.g. `topic` alongside `deckId`).
 * - If the id is the ONLY argument left, it is kept (shortened) instead of
 *   leaving the approver with nothing to look at — the token binds to
 *   exactly this input, so hiding its only content would ask for a blind
 *   approval.
 * - Every value renders as words; arrays/objects are summarised by count,
 *   never JSON-dumped.
 */
export function summarizeApprovedInput(input: Record<string, unknown>): ApprovedInputResult {
  const originalKeyCount = Object.keys(input).length
  const withoutPlumbing = Object.entries(input).filter(
    ([key]) => !ALWAYS_HIDDEN_KEYS.has(key.toLowerCase()),
  )

  if (withoutPlumbing.length === 0) {
    return { args: [], allHidden: originalKeyCount > 0 }
  }

  const meaningful = withoutPlumbing.filter(([key, value]) => !isOpaqueIdField(key, value))
  const idOnly = withoutPlumbing.filter(([key, value]) => isOpaqueIdField(key, value))

  if (meaningful.length > 0) {
    return {
      args: meaningful.map(([key, value]) => ({
        key,
        label: argKeyLabel(key),
        value: describeArgValue(value, key),
      })),
      allHidden: false,
    }
  }

  // Every remaining argument is an opaque id — it IS the subject, so show
  // it (shortened) rather than nothing.
  return {
    args: idOnly.map(([key, value]) => ({
      key,
      label: argKeyLabel(key),
      value: shortenId(value as string),
    })),
    allHidden: false,
  }
}
